/**
 * DistributionPanel —— 淨利分配**過帳**（P2-2；`decisions/010`）
 *
 * 政策（產品負責人 2026-09-25）：醫療基金 15%／CoLeadery 10–20%／CoLinkery 10%／
 * 市場推廣特別帳 40–50%（合共 85%）＋ 85 AI Technology 15%（合計 100%）。
 * ⚠️ 要**已鎖帳** + **一期一次**；分配＝權益撥帳（唔准入損益表）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, fmtMoney, type DistributionPreview, type DistributionRow, type Meta } from '../lib/api';

export default function DistributionPanel({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [coleadery, setColeadery] = useState('20');
  const [marketing, setMarketing] = useState('40');
  const [pv, setPv] = useState<DistributionPreview | null>(null);
  const [history, setHistory] = useState<DistributionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(() => {
    if (!entityId) return;
    api.distributions(entityId)
      .then((r) => setHistory(r.distributions))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const doPreview = async () => {
    setErr(null); setOk(null); setBusy(true);
    try {
      setPv(await api.distributionPreview(entityId, period, { coleadery, marketing }));
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setPv(null); } finally { setBusy(false); }
  };

  const doPost = async () => {
    setErr(null); setOk(null); setBusy(true);
    try {
      const r = await api.postDistribution({
        entityId, period, coleadery: Number(coleadery), marketing: Number(marketing),
      });
      setOk(t('dist.posted', { no: r.voucherNo, amount: fmtMoney(r.totalCents) }));
      setPv(null);
      loadHistory();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fields">
        <label className="field">
          <span>{t('dist.entity')}</span>
          <select value={entityId} onChange={(e) => { setEntityId(e.target.value); setPv(null); }}>
            {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{t('dist.period')}</span>
          <input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); setPv(null); }} />
        </label>
        <label className="field">
          <span>{t('dist.coleadery_pct')}</span>
          <input inputMode="numeric" value={coleadery} placeholder="10–20"
            onChange={(e) => { setColeadery(e.target.value); setPv(null); }} />
        </label>
        <label className="field">
          <span>{t('dist.marketing_pct')}</span>
          <input inputMode="numeric" value={marketing} placeholder="40–50"
            onChange={(e) => { setMarketing(e.target.value); setPv(null); }} />
        </label>
      </div>
      <p className="note">{t('dist.note')}</p>
      <button onClick={() => void doPreview()} disabled={busy}>{t('dist.preview')}</button>
      {err && <p className="err">{err}</p>}
      {ok && <p className="ok">{ok}</p>}

      {pv && (
        <>
          {(pv.ruleErrors ?? []).length > 0 && <p className="err">{pv.ruleErrors.join('；')}</p>}
          <p className="totals">
            {t('dist.income')}：<b>{fmtMoney(pv.incomeCents)}</b>　
            {t('dist.expense')}：<b>{fmtMoney(pv.expenseCents)}</b>　
            {t('dist.net')}：<b className={pv.netCents < 0 ? 'neg' : 'pos'}>{fmtMoney(pv.netCents)}</b>　
            {pv.locked ? <span className="ok">{t('dist.locked')}</span> : <span className="neg">{t('dist.not_locked')}</span>}
          </p>
          <table className="ledger">
            <thead>
              <tr>
                <th>{t('dist.target')}</th><th>{t('dist.code')}</th>
                <th className="num">%</th><th className="num">{t('dist.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {pv.items.map((i) => (
                <tr key={i.code}>
                  <td>{i.name}</td><td>{i.code}</td>
                  <td className="num">{i.pct}%</td>
                  <td className="num"><b>{fmtMoney(i.amountCents)}</b></td>
                </tr>
              ))}
              <tr>
                <th colSpan={3}>{t('dist.total')}</th>
                <td className="num"><b>{fmtMoney(pv.items.reduce((s, i) => s + i.amountCents, 0))}</b></td>
              </tr>
            </tbody>
          </table>
          {pv.alreadyPosted && <p className="err">{t('dist.already', { amount: fmtMoney(pv.alreadyPosted.totalCents) })}</p>}
          {!pv.alreadyPosted && pv.netCents <= 0 && <p className="err">{t('dist.loss')}</p>}
          <button className="cta" onClick={() => void doPost()} disabled={busy || !pv.canPost}>
            {t('dist.post')}
          </button>
          <p className="note">{t('dist.post_note')}</p>
        </>
      )}

      {history.length > 0 && (
        <>
          <h3>{t('dist.history')}</h3>
          <ul className="list">
            {history.map((d) => (
              <li key={d.id}>
                <span className="tag">{d.period}</span>
                <Link to={`/vouchers/${(d as { voucher_id?: string }).voucher_id ?? ''}`}>{d.voucher_no}</Link>
                <span>{t('dist.base_net')} {fmtMoney(Number(d.base_net_cents))}</span>
                <b>{fmtMoney(Number(d.total_cents))}</b>
                <span className="note">{d.lines.map((l) => `${l.target_name} ${l.pct_bp / 100}%`).join('、')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
